import React, { useState, useRef, useEffect } from 'react';
import SignatureModal from '../SignatureModal';

interface CreditCardAuthorizationProps {
  onAccept: (signature: string) => void;
}

const CreditCardAuthorization: React.FC<CreditCardAuthorizationProps> = ({ onAccept }) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signature, setSignature] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
      setHasScrolledToBottom(isAtBottom);
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreed(e.target.checked);
  };

  const handleSignatureComplete = (signatureData: string, signatureType: 'drawn' | 'typed', typedName?: string) => {
    setSignature(signatureData);
    setShowSignatureModal(false);
  };

  const handleSubmit = () => {
    if (signature) {
      onAccept(signature);
    }
  };

  return (
    <div className="app">
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        background: '#28527A',
        borderRadius: '16px',
        padding: '2rem',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        marginTop: '2rem'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ color: '#f9fafb', fontSize: '2rem', margin: '0 0 0.5rem 0' }}>
            The Real Therapists of Atlanta
          </h1>
          <p style={{ color: '#6CA6C1', margin: '0 0 1rem 0', fontSize: '1rem' }}>
            626 Main St., Palmetto, GA. 30268 | 470.250.9113
          </p>
          <h1 style={{ color: '#f9fafb', fontSize: '1.75rem', margin: '0 0 0.5rem 0' }}>
            Credit Card Authorization
          </h1>
          <p style={{ color: '#6CA6C1', margin: 0 }}>
            Please read the authorization form below and scroll to the bottom to continue
          </p>
        </div>

        {/* Authorization Content */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          border: '1px solid #6CA6C1',
          marginBottom: '2rem'
        }}>
          <div
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{
              height: '400px',
              overflowY: 'auto',
              padding: '2rem',
              color: '#d1d5db',
              fontSize: '0.9rem',
              lineHeight: '1.6'
            }}
          >
            <h2 style={{ color: '#f9fafb', marginBottom: '1.5rem' }}>
              CREDIT CARD AUTHORIZATION
            </h2>

            <p style={{ marginBottom: '2rem', fontSize: '1rem', lineHeight: '1.7' }}>
              By your electronic signature of this form, you authorize charges to your credit card through Stripe via My ClinicCore for services rendered. These charges will appear on your bank/credit card statement as <strong style={{ color: '#f9fafb' }}>[STATEMENT DESCRIPTOR]</strong>. You have the right to request a paper copy of this document.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              AUTHORIZATION
            </h3>
            <p style={{ marginBottom: '2rem', fontSize: '1rem', lineHeight: '1.7' }}>
              I authorize <strong style={{ color: '#f9fafb' }}>[CUSTOMER BUSINESS NAME]</strong> to charge my credit card through Stripe. <em style={{ color: '#6CA6C1' }}>[OPTIONAL CANCELLATION POLICY: I also agree that my credit card can be charged for any session that is not cancelled at least 48 hours prior to the scheduled session]</em>.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              TERMS AND CONDITIONS
            </h3>
            <p style={{ marginBottom: '1.5rem', fontSize: '1rem', lineHeight: '1.7' }}>
              I understand that this authorization will remain in effect until I cancel it in writing, and I agree to notify <strong style={{ color: '#f9fafb' }}>[BUSINESS NAME]</strong> in writing of any changes in my account information or termination of this authorization.
            </p>

            <p style={{ marginBottom: '1.5rem', fontSize: '1rem', lineHeight: '1.7' }}>
              I certify that I am an authorized user of this credit card and will not dispute these scheduled transactions with my bank or credit card company as long as the transactions correspond to the terms indicated in this authorization form. I acknowledge that credit card transactions could be linked to Protected Health Information.
            </p>

            <h3 style={{ color: '#f9fafb', marginTop: '2rem', marginBottom: '1rem' }}>
              IMPORTANT INFORMATION
            </h3>
            <ul style={{ marginBottom: '2rem', paddingLeft: '1.5rem', color: '#d1d5db' }}>
              <li style={{ marginBottom: '0.75rem' }}>This authorization allows automatic charging for services</li>
              <li style={{ marginBottom: '0.75rem' }}>You will receive notification of charges via email receipt</li>
              <li style={{ marginBottom: '0.75rem' }}>You may cancel this authorization at any time in writing</li>
              <li style={{ marginBottom: '0.75rem' }}>All charges are processed securely through Stripe</li>
              <li style={{ marginBottom: '0.75rem' }}>Your payment information is encrypted and protected</li>
            </ul>

            <p style={{ marginBottom: '1.5rem', fontWeight: 'bold', color: '#f9fafb', textTransform: 'uppercase' }}>
              BY SIGNING BELOW I AM AGREEING THAT I HAVE READ, UNDERSTOOD AND AGREE TO THE ITEMS CONTAINED IN THIS DOCUMENT.
            </p>

            <p style={{ marginBottom: '0', fontSize: '0.8rem', color: '#d1d5db' }}>
              Last updated: {new Date().toLocaleDateString()}
            </p>
          </div>
        </div>

        {/* Agreement Section */}
        <div style={{
          background: '#113F67',
          borderRadius: '12px',
          padding: '1.5rem',
          border: '1px solid #6CA6C1',
          marginBottom: '1.5rem'
        }}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              color: hasScrolledToBottom ? '#f9fafb' : '#6b7280',
              cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
            }}>
              <input
                type="checkbox"
                checked={isAgreed}
                onChange={handleCheckboxChange}
                disabled={!hasScrolledToBottom}
                style={{
                  marginRight: '0.75rem',
                  width: '18px',
                  height: '18px',
                  cursor: hasScrolledToBottom ? 'pointer' : 'not-allowed'
                }}
              />
              I have read, understood, and agree to the Credit Card Authorization
            </label>
          </div>

          {/* Signature Section */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              color: isAgreed ? '#f9fafb' : '#6b7280',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>
              Digital Signature *
            </label>
            <button
              type="button"
              onClick={() => setShowSignatureModal(true)}
              disabled={!isAgreed}
              style={{
                width: '100%',
                padding: '3rem 1rem',
                borderRadius: '8px',
                border: '2px dashed #6CA6C1',
                background: isAgreed ? '#374151' : '#1f2937',
                color: isAgreed ? '#f9fafb' : '#6b7280',
                fontSize: '1rem',
                cursor: isAgreed ? 'pointer' : 'not-allowed',
                textAlign: 'center',
                transition: 'all 0.3s ease'
              }}
            >
              {signature ? (
                <div>
                  <div style={{ marginBottom: '0.5rem', color: '#10b981' }}>✓ Signature Captured</div>
                  <div style={{ fontSize: '0.875rem' }}>Click to update signature</div>
                </div>
              ) : (
                <div>
                  <div style={{ marginBottom: '0.5rem' }}>📝</div>
                  <div>Click to add your digital signature</div>
                </div>
              )}
            </button>
          </div>
        </div>

        {/* Submit Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleSubmit}
            disabled={!signature}
            style={{
              background: signature
                ? 'linear-gradient(135deg, #D4A017 0%, #B8860B 100%)'
                : '#6b7280',
              color: 'white',
              padding: '0.75rem 3rem',
              fontSize: '1rem',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: signature ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              boxShadow: signature
                ? '0 4px 14px 0 rgba(212, 160, 23, 0.3)'
                : 'none'
            }}
          >
            Submit Authorization
          </button>
        </div>

        {/* Progress Indicator */}
        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          padding: '1rem',
          background: '#065f46',
          borderRadius: '8px',
          border: '1px solid #10b981'
        }}>
          <div style={{ color: '#d1d5db', fontSize: '0.875rem' }}>
            Progress:
            <span style={{ color: hasScrolledToBottom ? '#10b981' : '#6b7280', marginLeft: '0.5rem' }}>
              ✓ Read Authorization
            </span>
            <span style={{ color: isAgreed ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Accept Terms
            </span>
            <span style={{ color: signature ? '#10b981' : '#6b7280', marginLeft: '1rem' }}>
              ✓ Digital Signature
            </span>
          </div>
        </div>
      </div>

      {/* Signature Modal */}
      {showSignatureModal && (
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={handleSignatureComplete}
          title="Credit Card Authorization Signature"
          clientName="Client"
          previousSignature={signature ? {
            signatureData: signature,
            signatureType: 'drawn' as const
          } : undefined}
        />
      )}
    </div>
  );
};

export default CreditCardAuthorization;